import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Award,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Edit3,
  Gauge,
  Loader2,
  RefreshCw,
  Save,
  Search,
  X,
} from "lucide-react";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const PRIMARY_RATE_FIELDS = [
  ["ordinary", "Ordinary"],
  ["evening", "Afternoon / Evening"],
  ["night", "Night"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
  ["public_holiday", "Public Holiday"],
];

const ADDITIONAL_RATE_FIELDS = [
  ["overtime_first_2_hours", "Overtime — First 2 Hours"],
  ["overtime_after_2_hours", "Overtime — After 2 Hours"],
  ["overtime_public_holiday", "Overtime — Public Holiday"],
  [
    "broken_shift_beyond_12_hours",
    "Broken Shift — Beyond 12 Hours",
  ],
  ["remote_weekday_6am_8pm", "Remote Weekday 6am–8pm"],
  [
    "remote_weekday_first_2_outside_span",
    "Remote Weekday — First 2 Outside Span",
  ],
  [
    "remote_weekday_after_2_outside_span",
    "Remote Weekday — After 2 Outside Span",
  ],
  ["remote_saturday_6am_8pm", "Remote Saturday 6am–8pm"],
  [
    "remote_saturday_first_2_outside_span",
    "Remote Saturday — First 2 Outside Span",
  ],
  [
    "remote_saturday_after_2_outside_span",
    "Remote Saturday — After 2 Outside Span",
  ],
  ["remote_sunday", "Remote Sunday"],
  ["remote_public_holiday", "Remote Public Holiday"],
  [
    "remote_over_10_hours_first_2",
    "Remote Over 10 Hours — First 2",
  ],
  [
    "remote_over_10_hours_after_2",
    "Remote Over 10 Hours — After 2",
  ],
];

export default function AwardRates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAwardRates = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get("/award-rate-sets");
      setRows(response.data || []);
    } catch (error) {
      console.error("Failed to load award rates", error);
      toast.error(
        error?.response?.data?.detail ||
          "Failed to load award rates"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAwardRates();
  }, [loadAwardRates]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return rows;

    return rows.filter((row) => {
      const searchable = [
        row.display_name,
        row.award_code,
        row.award_name,
        row.classification_label,
        row.employment_type,
        row.stream,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [rows, search]);

  const startEditing = (row) => {
    setEditing({
      id: row.id,
      display_name: row.display_name || "",
      effective_from: row.effective_from || "",
      effective_to: row.effective_to || "",
      mileage_per_km:
        row.allowances?.mileage_per_km ?? 1.01,
      rates: {
        ...(row.rates || {}),
      },
      xero_earnings_rates: {
        ...(row.xero_earnings_rates || {}),
      },
      xero_earnings_rate_ids: {
        ...(row.xero_earnings_rate_ids || {}),
      },
    });
  };

  const updateEditingRate = (field, value) => {
    setEditing((current) => ({
      ...current,
      rates: {
        ...current.rates,
        [field]: value,
      },
    }));
  };

  const updateXeroMapping = (field, value) => {
    setEditing((current) => ({
      ...current,
      xero_earnings_rates: {
        ...current.xero_earnings_rates,
        [field]: value,
      },
    }));
  };

  const updateXeroRateId = (field, value) => {
    setEditing((current) => ({
      ...current,
      xero_earnings_rate_ids: {
        ...current.xero_earnings_rate_ids,
        [field]: value,
      },
    }));
  };

  const saveEditing = async () => {
    if (!editing?.id) return;

    setSaving(true);

    try {
      const numericRates = {};

      for (const [key, value] of Object.entries(
        editing.rates || {}
      )) {
        const number = Number(value);

        if (!Number.isFinite(number) || number < 0) {
          toast.error(`Invalid rate entered for ${key}`);
          setSaving(false);
          return;
        }

        numericRates[key] = number;
      }

      const mileage = Number(editing.mileage_per_km);

      if (!Number.isFinite(mileage) || mileage < 0) {
        toast.error("Invalid mileage allowance");
        setSaving(false);
        return;
      }

      await api.put(`/award-rate-sets/${editing.id}`, {
        display_name: editing.display_name,
        effective_from: editing.effective_from,
        effective_to: editing.effective_to || null,
        rates: numericRates,
        allowances: {
          mileage_per_km: mileage,
        },
        xero_earnings_rates:
          editing.xero_earnings_rates || {},
        xero_earnings_rate_ids:
          editing.xero_earnings_rate_ids || {},
      });

      toast.success("Award rate set updated");
      setEditing(null);
      await loadAwardRates();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Failed to update award rate set"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-4 md:p-6 space-y-6"
      data-testid="award-rates-page"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Award className="h-7 w-7 text-primary" />
            <h1 className="text-2xl font-bold">Award Rates</h1>
          </div>

          <p className="mt-1 text-sm text-muted-foreground">
            Manage selectable award classifications, penalty
            rates, mileage allowances and Xero earnings mappings.
          </p>
        </div>

        <button
          type="button"
          onClick={loadAwardRates}
          disabled={loading}
          className="inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${
              loading ? "animate-spin" : ""
            }`}
          />
          Refresh
        </button>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-semibold">
              SCHADS Social and Community Services — Casual
            </p>
            <p className="text-sm text-muted-foreground">
              MA000100 · Effective from 1 July 2026
            </p>
          </div>

          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />

            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search classification"
              className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm"
              data-testid="award-rate-search"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[280px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-xl border bg-card py-16 text-center">
          <Award className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="font-semibold">
            No award classifications found
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRows.map((row) => {
            const expanded = expandedId === row.id;

            return (
              <div
                key={row.id}
                className="rounded-xl border bg-card"
                data-testid={`award-rate-${row.id}`}
              >
                <div className="p-4 md:p-5">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-semibold">
                          {row.display_name}
                        </h2>

                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                          Active
                        </span>
                      </div>

                      <p className="mt-1 text-sm text-muted-foreground">
                        {row.classification_label} ·{" "}
                        {row.employment_type === "casual"
                          ? "Casual"
                          : row.employment_type}
                      </p>

                      <p className="mt-1 text-xs text-muted-foreground">
                        Effective {formatDate(row.effective_from)}
                        {row.effective_to
                          ? ` to ${formatDate(row.effective_to)}`
                          : ""}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startEditing(row)}
                        className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
                      >
                        <Edit3 className="mr-2 h-4 w-4" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setExpandedId(expanded ? null : row.id)
                        }
                        className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
                      >
                        {expanded ? (
                          <ChevronUp className="mr-2 h-4 w-4" />
                        ) : (
                          <ChevronDown className="mr-2 h-4 w-4" />
                        )}
                        {expanded ? "Hide Details" : "View Details"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-7">
                    {PRIMARY_RATE_FIELDS.map(([key, label]) => (
                      <RateMetric
                        key={key}
                        label={label}
                        value={row.rates?.[key]}
                      />
                    ))}

                    <div className="rounded-lg bg-muted p-3">
                      <Gauge className="mb-1 h-4 w-4 text-primary" />
                      <p className="text-xs text-muted-foreground">
                        Mileage
                      </p>
                      <p className="mt-1 font-semibold">
                        {formatCurrency(
                          row.allowances?.mileage_per_km
                        )}
                        /km
                      </p>
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t p-4 md:p-5">
                    <h3 className="mb-3 font-semibold">
                      Overtime and Remote Work Rates
                    </h3>

                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                      {ADDITIONAL_RATE_FIELDS.map(
                        ([key, label]) => (
                          <RateMetric
                            key={key}
                            label={label}
                            value={row.rates?.[key]}
                          />
                        )
                      )}
                    </div>

                    <h3 className="mb-3 mt-6 font-semibold">
                      Xero Earnings Rate Mapping
                    </h3>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {[
                        ...PRIMARY_RATE_FIELDS,
                        ["mileage", "Mileage"],
                      ].map(([key, label]) => (
                        <div
                          key={key}
                          className="rounded-lg border p-3"
                        >
                          <p className="text-xs text-muted-foreground">
                            {label}
                          </p>
                          <p className="mt-1 text-sm font-medium">
                            {row.xero_earnings_rates?.[key] ||
                              "Name not mapped"}
                          </p>
                          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                            Xero ID:{" "}
                            {row.xero_earnings_rate_ids?.[
                              key
                            ] || "Not linked"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-xl border bg-background p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">
                  Edit Award Rate Set
                </h2>
                <p className="text-sm text-muted-foreground">
                  {editing.display_name}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md border p-2 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="Display Name">
                <input
                  value={editing.display_name}
                  onChange={(event) =>
                    setEditing((current) => ({
                      ...current,
                      display_name: event.target.value,
                    }))
                  }
                  className="w-full rounded-md border bg-background px-3 py-2"
                />
              </Field>

              <Field label="Effective From">
                <input
                  type="date"
                  value={editing.effective_from}
                  onChange={(event) =>
                    setEditing((current) => ({
                      ...current,
                      effective_from: event.target.value,
                    }))
                  }
                  className="w-full rounded-md border bg-background px-3 py-2"
                />
              </Field>

              <Field label="Mileage Allowance per KM">
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editing.mileage_per_km}
                    onChange={(event) =>
                      setEditing((current) => ({
                        ...current,
                        mileage_per_km: event.target.value,
                      }))
                    }
                    className="w-full rounded-md border bg-background py-2 pl-9 pr-3"
                  />
                </div>
              </Field>
            </div>

            <h3 className="mb-3 mt-6 font-semibold">
              Primary Rates
            </h3>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {PRIMARY_RATE_FIELDS.map(([key, label]) => (
                <MoneyInput
                  key={key}
                  label={label}
                  value={editing.rates?.[key]}
                  onChange={(value) =>
                    updateEditingRate(key, value)
                  }
                />
              ))}
            </div>

            <h3 className="mb-3 mt-6 font-semibold">
              Overtime and Remote Work
            </h3>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {ADDITIONAL_RATE_FIELDS.map(([key, label]) => (
                <MoneyInput
                  key={key}
                  label={label}
                  value={editing.rates?.[key]}
                  onChange={(value) =>
                    updateEditingRate(key, value)
                  }
                />
              ))}
            </div>

            <h3 className="mb-3 mt-6 font-semibold">
              Xero Earnings Rate Names
            </h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                ...PRIMARY_RATE_FIELDS,
                ["mileage", "Mileage"],
              ].map(([key, label]) => (
                <div
                  key={key}
                  className="space-y-3 rounded-lg border p-3"
                >
                  <Field label={`${label} — Xero Rate Name`}>
                    <input
                      value={
                        editing.xero_earnings_rates?.[
                          key
                        ] || ""
                      }
                      onChange={(event) =>
                        updateXeroMapping(
                          key,
                          event.target.value
                        )
                      }
                      placeholder="Name shown in Xero"
                      className="w-full rounded-md border bg-background px-3 py-2"
                    />
                  </Field>

                  <Field label={`${label} — Xero Earnings Rate ID`}>
                    <input
                      value={
                        editing.xero_earnings_rate_ids?.[
                          key
                        ] || ""
                      }
                      onChange={(event) =>
                        updateXeroRateId(
                          key,
                          event.target.value
                        )
                      }
                      placeholder="Xero earnings-rate UUID"
                      className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
                    />
                  </Field>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t pt-5">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={saveEditing}
                disabled={saving}
                className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Award Rates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RateMetric({ label, value }) {
  return (
    <div className="rounded-lg bg-muted p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-semibold">
        {formatCurrency(value)}
      </p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="space-y-2">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}

function MoneyInput({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div className="relative">
        <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <input
          type="number"
          min="0"
          step="0.01"
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-md border bg-background py-2 pl-9 pr-3"
        />
      </div>
    </Field>
  );
}
